/**
 * @fileoverview Brand-Aligned Authentication Page
 * @module AuthPage
 * 
 * Business Context:
 * - Main entry point for user authentication
 * - Handles both login and registration flows
 * - Features Texas-themed branding matching client app landing page
 * 
 * Design Context:
 * - Consistent with client LandingPage brand aesthetic (teal accent)
 * - Clean, minimal layout that fits viewport without scrolling
 * - Responsive design optimized for all breakpoints
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LoginForm } from '../components/auth/LoginForm';
import { ForgotPasswordForm } from '../components/auth/ForgotPasswordForm';
import { Star, Check } from 'lucide-react';

type AuthMode = 'login' | 'forgot-password';

export const AuthPage: React.FC = () => {
  const [authMode, setAuthMode] = useState<AuthMode>('login');

  // Load Plus Jakarta Sans font (matching landing page)
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  const handleSwitchToLogin = () => setAuthMode('login');
  const handleForgotPassword = () => setAuthMode('forgot-password');

  return (
    <div className="h-screen flex overflow-hidden" style={{ fontFamily: "'Plus Jakarta Sans', Inter, sans-serif" }}>
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative">
        {/* Brand Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-teal-900"></div>
        
        {/* Subtle Dot Pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }} />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-10 xl:px-16 py-8 w-full">
          <div className="max-w-md">
            {/* Logo/Brand */}
            <div className="mb-6">
              <img 
                src="/logo-104.png" 
                alt="Appeal Your Home" 
                className="w-14 h-14 object-contain mb-4"
              />
              <h1 className="text-3xl xl:text-4xl font-extrabold text-white mb-2 leading-tight tracking-tight">
                Save Money on{' '}
                <span className="text-teal-400">Property Taxes</span>
              </h1>
              <p className="text-gray-400 font-medium">
                The complete Texas property tax appeal system
              </p>
            </div>

            {/* Value Proposition - Compact */}
            <div className="space-y-3 mb-6">
              {[
                { title: 'AI Argument Generator', desc: 'Our AI writes your negotiation script' },
                { title: 'Done-For-You Templates', desc: 'Pre-formatted forms ready to submit' },
                { title: 'Step-by-Step Video Course', desc: 'Expert training from filing to hearing' },
              ].map((item, i) => (
                <div key={i} className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-5 h-5 bg-teal-500/20 rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-teal-400" />
                  </div>
                  <div>
                    <span className="text-white font-semibold text-sm">{item.title}</span>
                    <span className="text-gray-500 text-sm"> — {item.desc}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Social Proof - Compact */}
            <div className="p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-teal-400 fill-current" />
                  ))}
                </div>
                <span className="text-white font-semibold text-sm">5.0 Average</span>
              </div>
              <p className="text-gray-300 text-sm italic leading-relaxed">
                "I saved $4,287 on my property taxes. The templates made it so easy!"
              </p>
              <div className="flex items-center justify-between mt-2">
                <span className="text-gray-500 text-xs">— Sarah M., Harris County</span>
                <span className="bg-teal-500/20 text-teal-400 text-xs font-bold px-2 py-0.5 rounded-full">
                  Saved $4,287/yr
                </span>
              </div>
            </div>

            {/* Trust Line */}
            <p className="text-gray-500 text-xs mt-4 text-center">
              Trusted by thousands of Texas homeowners • 95%+ Success Rate
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Auth Forms */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 lg:p-8 bg-gray-50">
        <div className="w-full max-w-sm">
          {/* Mobile Logo - Premium brand treatment matching LandingPage */}
          <div className="lg:hidden flex flex-col items-center mb-6">
            <img 
              src="/logo-104.png" 
              alt="" 
              className="w-12 h-12 object-contain mb-2"
            />
            <div className="flex flex-col items-center">
              <span 
                className="text-[1.1rem] leading-[1.2] tracking-[-0.015em] text-gray-900"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                <span className="font-semibold">Appeal Your</span>
                {' '}
                <span className="font-extrabold text-teal-600">Home</span>
              </span>
              <span 
                className="text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-gray-400 mt-1"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                Learn. Advocate. Save.
              </span>
            </div>
          </div>

          {/* Auth Forms */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 lg:p-8">
            {authMode === 'login' && (
              <LoginForm 
                onForgotPassword={handleForgotPassword}
              />
            )}

            {authMode === 'forgot-password' && (
              <ForgotPasswordForm onBackToLogin={handleSwitchToLogin} />
            )}
          </div>

          {/* Additional Info */}
          <div className="text-center mt-4 text-xs text-gray-500">
            <p>
              By signing in, you agree to our{' '}
              <Link to="/terms" className="text-gray-700 hover:text-teal-600 font-medium" target="_blank">Terms</Link> and{' '}
              <Link to="/privacy" className="text-gray-700 hover:text-teal-600 font-medium" target="_blank">Privacy Policy</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
